import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  supportTickets as mockTickets,
  agentList,
  priorityLabels,
  statusLabels,
  type SupportTicket,
  type TicketPriority,
  type TicketStatus,
  type TicketMessage,
} from '@/mocks/superAdminSupport';
import SupportFilters from './components/SupportFilters';
import SupportTable from './components/SupportTable';
import TicketDetailModal from './components/TicketDetailModal';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<SupportTicket[]> {
  return new Promise((resolve, reject) => {
    const delay = 800 + Math.random() * 500;
    setTimeout(() => {
      if (Math.random() > 0.93) {
        reject(new Error('Timeout al conectar con el servidor. Verificá tu conexión.'));
      } else {
        resolve(mockTickets);
      }
    }, delay);
  });
}

function showAlert(icon: 'success' | 'error' | 'warning' | 'info', title: string, text?: string) {
  return Swal.fire({
    icon,
    title,
    text: text ?? '',
    confirmButtonColor: '#1a1a1a',
    customClass: {
      popup: 'rounded-2xl text-sm',
      title: 'text-sm font-bold',
      confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
    },
  });
}

export default function SuperAdminSupport() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState('todos');
  const [activePriority, setActivePriority] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewTicket, setViewTicket] = useState<SupportTicket | null>(null);
  const navigate = useNavigate();

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((data) => {
        setTickets(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Counts
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: tickets.length };
    ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'].forEach((st) => {
      counts[st] = tickets.filter((t) => t.status === st).length;
    });
    return counts;
  }, [tickets]);

  const priorityCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: tickets.length };
    ['low', 'normal', 'high', 'urgent'].forEach((p) => {
      counts[p] = tickets.filter((t) => t.priority === p).length;
    });
    return counts;
  }, [tickets]);

  // Filtered
  const filteredTickets = useMemo(() => {
    let result = tickets;
    if (activeStatus !== 'todos') result = result.filter((t) => t.status === activeStatus);
    if (activePriority !== 'todos') result = result.filter((t) => t.priority === activePriority);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.ticketNumber.toLowerCase().includes(q) ||
          t.radioName.toLowerCase().includes(q) ||
          t.subject.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q),
      );
    }
    return result;
  }, [tickets, activeStatus, activePriority, searchTerm]);

  // KPIs
  const kpis = useMemo(() => {
    const open = tickets.filter((t) => t.status === 'open').length;
    const inProgress = tickets.filter((t) => t.status === 'in_progress').length;
    const unassigned = tickets.filter((t) => !t.assignedTo && t.status !== 'closed' && t.status !== 'resolved').length;
    const urgent = tickets.filter((t) => t.priority === 'urgent' && t.status !== 'closed' && t.status !== 'resolved').length;
    return { open, inProgress, unassigned, urgent };
  }, [tickets]);

  // --- Ticket actions ---

  const handleReply = (ticketId: number, message: string) => {
    const newMsg: TicketMessage = {
      id: Date.now(),
      from: 'support',
      author: 'Carlos Mendoza',
      content: message,
      date: new Date().toISOString().replace('T', ' ').slice(0, 19),
    };
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, messages: [...t.messages, newMsg], updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
          : t,
      ),
    );
    showAlert('success', 'Respuesta enviada', 'Tu mensaje fue agregado a la conversación.');
  };

  const handleChangePriority = (ticketId: number, newPriority: TicketPriority) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, priority: newPriority, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
          : t,
      ),
    );
    showAlert('success', 'Prioridad actualizada', `El ticket ahora tiene prioridad ${priorityLabels[newPriority].toLowerCase()}.`);
  };

  const handleChangeStatus = (ticketId: number, newStatus: TicketStatus) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, status: newStatus, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
          : t,
      ),
    );
    showAlert('success', 'Estado actualizado', `El ticket ahora está "${statusLabels[newStatus]}".`);
  };

  const handleAssign = (ticketId: number, agentId: string) => {
    const agent = agentList.find((a) => a.id === agentId);
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, assignedTo: agentId, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
          : t,
      ),
    );
    showAlert('success', 'Agente asignado', `${agent?.name ?? 'Agente'} fue asignado al ticket.`);
  };

  const handleCloseTicket = (ticketId: number) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, status: 'closed' as TicketStatus, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
          : t,
      ),
    );
    showAlert('success', 'Ticket cerrado', 'El ticket fue cerrado exitosamente.');
  };

  const handleTransferToLive = (ticket: SupportTicket) => {
    const transferId = Date.now();
    const transferData = {
      id: transferId,
      ticketNumber: ticket.ticketNumber,
      ticketId: ticket.id,
      radioName: ticket.radioName,
      frequency: ticket.frequency,
      planName: 'Business',
      subject: ticket.subject,
      category: ticket.category,
      messages: ticket.messages.map((m) => ({
        id: m.id + transferId,
        from: m.from === 'support' ? 'sa' as const : 'radio' as const,
        author: m.author,
        radioName: ticket.radioName,
        content: m.content,
        timestamp: m.date,
      })),
      transferredAt: new Date().toISOString().replace('T', ' ').slice(0, 16),
    };

    // Guardar en localStorage
    const existing = JSON.parse(localStorage.getItem('sa_live_transfers') || '[]');
    existing.push(transferData);
    localStorage.setItem('sa_live_transfers', JSON.stringify(existing));

    // Marcar ticket con nota de transferencia
    const transferNote: TicketMessage = {
      id: Date.now(),
      from: 'support',
      author: 'Carlos Mendoza',
      content: `Ticket transferido a Live Chat. Conversación creada con ${ticket.radioName}. Podés encontrarla en /sa/live-support. Transfer ID: ${transferId}`,
      date: new Date().toISOString().replace('T', ' ').slice(0, 19),
    };
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticket.id
          ? { ...t, messages: [...t.messages, transferNote], updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
          : t,
      ),
    );

    Swal.fire({
      icon: 'success',
      title: '¡Transferido a Live Chat!',
      html: `La conversación con <strong>${ticket.radioName}</strong> fue creada en Live Support.<br><br>¿Querés ir ahora al Live Chat para atenderla?`,
      showCancelButton: true,
      confirmButtonText: 'Ir a Live Chat',
      cancelButtonText: 'Seguir en Soporte',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setViewTicket(null);
        navigate('/sa/live-support');
      }
    });
  };

  const totalActivos = tickets.filter((t) => t.status !== 'closed').length;
  const totalCerrados = tickets.filter((t) => t.status === 'closed').length;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
          <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
          <i className="ri-arrow-right-s-line text-[9px]"></i>
          <span className="text-foreground-500 font-medium">Soporte Global</span>
        </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Soporte Global</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${filteredTickets.length} de ${tickets.length} tickets · ${totalActivos} activos · ${totalCerrados} cerrados`
                    : 'Cargando datos...'}
                </p>
              </div>

              <div className="relative w-full sm:w-56">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar ticket..."
                  disabled={loadingState !== 'ready'}
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* KPI cards */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: 'Abiertos', value: kpis.open, icon: 'ri-checkbox-blank-circle-line', color: 'text-secondary-500', bg: 'bg-secondary-50' },
                  { label: 'En Progreso', value: kpis.inProgress, icon: 'ri-loader-4-line', color: 'text-sky-500', bg: 'bg-sky-50' },
                  { label: 'Sin Asignar', value: kpis.unassigned, icon: 'ri-user-unfollow-line', color: 'text-amber-500', bg: 'bg-amber-50' },
                  { label: 'Urgentes', value: kpis.urgent, icon: 'ri-alert-line', color: 'text-red-400', bg: 'bg-red-50' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center shrink-0`}>
                      <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-[10px] font-medium">{stat.label}</p>
                      <p className="text-foreground-800 text-sm font-bold">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3 animate-pulse">
                    <div className="w-9 h-9 rounded-lg bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-3 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* Info banner */}
            {loadingState === 'ready' && (
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <i className="ri-customer-service-2-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-accent-800 text-xs font-semibold">Centro de Soporte Global</p>
                    <p className="text-accent-600/80 text-[10px]">Gestioná tickets de todas las radios desde un solo lugar. Asigná agentes, cambiá prioridades y resolvé incidencias.</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1">
                  <i className="ri-information-line text-[9px]"></i>
                  Modo simulación
                </span>
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <SupportFilters
                activeStatus={activeStatus}
                onStatusChange={setActiveStatus}
                activePriority={activePriority}
                onPriorityChange={setActivePriority}
                statusCounts={statusCounts}
                priorityCounts={priorityCounts}
              />
            )}

            {/* Table */}
            <SupportTable tickets={filteredTickets} onView={setViewTicket} />

            {/* ══════ NAVEGACIÓN RÁPIDA ══════ */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
                <h3 className="text-foreground-950 text-xs font-bold mb-2.5 flex items-center gap-2">
                  <i className="ri-compass-3-line text-accent-500"></i>
                  Navegación Rápida
                </h3>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[
                    { label: 'Radios', icon: 'ri-radio-line', path: '/sa/radios' },
                    { label: 'Contratos', icon: 'ri-file-text-line', path: '/sa/contracts' },
                    { label: 'Invoices', icon: 'ri-file-list-3-line', path: '/sa/invoices' },
                    { label: 'Pagos', icon: 'ri-money-dollar-circle-line', path: '/sa/payments' },
                    { label: 'Billing', icon: 'ri-bank-card-line', path: '/sa/billing' },
                    { label: 'Frecuencias', icon: 'ri-radar-line', path: '/sa/frequencies' },
                    { label: 'Planes', icon: 'ri-stack-line', path: '/sa/plans' },
                  ].map((nav) => (
                    <Link
                      key={nav.path}
                      to={nav.path}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${nav.icon} text-[10px]`}></i>
                      {nav.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      <TicketDetailModal
        ticket={viewTicket}
        onClose={() => setViewTicket(null)}
        onReply={handleReply}
        onChangePriority={handleChangePriority}
        onChangeStatus={handleChangeStatus}
        onAssign={handleAssign}
        onCloseTicket={handleCloseTicket}
        onTransferToLive={handleTransferToLive}
      />

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Soporte Global
        </p>
      </div>
    </div>
  );
}